import React, { lazy, Suspense, useMemo } from 'react';
import {
	matchPath,
	Switch,
	useRouteMatch,
	Redirect,
	useLocation,
	generatePath,
	Route as ReactRoute
} from 'react-router-dom';
import Route from './route';
import { useFeatureAvailable, useSoftDeployAccess, useSelector, usePagesAuthoringAccess } from 'App/components/hooks';
import { RootState } from 'Store/index';
import type { AgentPage } from 'Resources/AgentPages';
import { Feature, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import AppService from 'App/legacy/AppService';
const MailTemplates = lazy(() => import('App/pages/MailTemplates'));
import MailTemplatesAllowed from 'App/pages/MailTemplates/allowed';
const ListUserDefinedObjects = lazy(() => import('App/pages/ListUserDefinedObjects'));
import ListUserDefinedObjectsAllowed from 'App/pages/ListUserDefinedObjects/allowed';
const GroupMailEditor = lazy(() => import('Components/GroupMailEditor'));
const MailTemplateEditor = lazy(() => import('Components/MailTemplateEditor'));
const DocumentTemplateEditor = lazy(() => import('Components/DocumentTemplateEditor'));
const DetectedDuplicates = lazy(() => import('Components/DetectedDuplicates'));
const DetectedDuplicatesLandingPage = lazy(() => import('App/components/DetectedDuplicatesLandingPage'));
const CalendarView = lazy(() => import('Components/CalendarView/CalendarView'));
const ChangeBrands = lazy(() => import('Components/Brands/ChangeBrands'));
const MarketingBoard = lazy(() => import('App/pages/MarketingBoard'));
import MarketingBoardAllowed from 'App/pages/MarketingBoard/allowed';
const Salesboard = lazy(() => import('App/pages/Salesboard/SalesboardRouter'));
const PageView = lazy(() => import('App/pages/PageView/PageView'));
import SalesboardAllowed from 'App/pages/Salesboard/allowed';
const FormEditor = lazy(() => import('Components/FormEditor'));
const FormOverview = lazy(() => import('Components/FormOverview'));
const TaskList = lazy(() => import('App/pages/TaskList'));
import TaskListAllowed from 'App/pages/TaskList/allowed';
const Prospecting = lazy(() => import('Components/Prospecting'));
import ProspectingAllowed from 'Components/Prospecting/allowed';
const Signals = lazy(() => import('App/pages/Prospecting/Signals'));
import SignalsAllowed from 'App/pages/Prospecting/Signals/allowed';
const EditSocialEvent = lazy(() => import('Components/SocialEvent/EditSocialEvent'));
import EditSocialEventAllowed from 'Components/SocialEvent/allowed';
const Reportcenter = lazy(() => import('App/pages/Reportcenter'));
import ReportcenterAllowed from 'App/pages/Reportcenter/allowed';
const ReportcenterPublic = lazy(() => import('App/pages/ReportcenterPublic'));
import history from './history';
import LoaderPage from './LoaderPage';
const ListCampaigns = lazy(() => import('../ListCampaigns'));
import CallListsAllowed from 'App/pages/ListCampaigns/CallListsAllowed';
const ListForms = lazy(() => import('App/pages/ListFormsAndLandingPages/ListForms'));
import ListFormsAllowed from 'App/pages/ListFormsAndLandingPages/allowed';
const ListLandingPages = lazy(() => import('App/pages/ListFormsAndLandingPages/ListLandingPages'));
import ListLandingPagesAllowed from 'App/pages/ListFormsAndLandingPages/allowed';
const ListSocialEvents = lazy(() => import('App/pages/ListSocialEvents'));
import ListSocialEventsAllowed from 'App/pages/ListSocialEvents/allowed';
import ListFlowsAllowed from '../ListFlows/allowed';
const ListFlows = lazy(() => import('../ListFlows'));
const ListMail = lazy(() => import('../ListMail'));
const ListMailCampaigns = lazy(() => import('../ListMailCampaigns'));
import ListMailCampaignsAllowed from '../ListMailCampaigns/allowed';
const AccountGrowth = lazy(() => import('App/pages/AccountGrowth'));
const CompanyGroupCard = lazy(() => import('App/pages/CompanyGroupCard'));
import CompanyGroupCardAllowed from 'App/pages/CompanyGroupCard/allowed';
const ListSegments = lazy(() => import('../ListSegments'));
const FormSubmits = lazy(() => import('App/pages/ListFormSubmits'));
import FormSubmitsAllowed from 'App/pages/ListFormSubmits/allowed';
const ListOrders = lazy(() => import('../ListOrders'));
import ListOrdersAllowed from 'App/pages/ListOrders/allowed';
const ListActivities = lazy(() => import('../ListActivities'));
import ListActivitiesAllowed from 'App/pages/ListActivities/allowed';
const ListAppointments = lazy(() => import('../ListAppointments'));
import ListAppointmentsAllowed from 'App/pages/ListAppointments/allowed';
const ListVisitors = lazy(() => import('App/pages/ListVisits'));
import ListVisitorsAllowed from 'App/pages/ListVisits/allowed';
const ListContacts = lazy(() => import('App/pages/ListContacts'));
const ListAgreements = lazy(() => import('App/pages/ListAgreements'));
const ListOpportunities = lazy(() => import('App/pages/ListOpportunities'));
import ListOpportunitiesAllowed from 'App/pages/ListOpportunities/allowed';
const ListPeriodizations = lazy(() => import('App/pages/ListPeriodizations'));
import ListPeriodizationsAllowed from 'App/pages/ListPeriodizations/allowed';
const ListPhoneCalls = lazy(() => import('App/pages/ListPhoneCalls'));
import ListPhoneCallsAllowed from 'App/pages/ListPhoneCalls/allowed';
const ListLeads = lazy(() => import('../ListLeads'));
import ListLeadsAllowed from '../ListLeads/allowed';
const ClientCard = lazy(() => import('../ClientCard'));
import ClientCardAllowed from '../ClientCard/allowed';
const ContactCard = lazy(() => import('../ContactCard'));
import ContactCardAllowed from '../ContactCard/allowed';
const CampaignCard = lazy(() => import('../CampaignCard'));
import CampaignCardAllowed from '../CampaignCard/allowed';
const CustomerPortfolio = lazy(() => import('../CustomerPortfolio'));
import CustomerPortfolioAllowed from '../CustomerPortfolio/allowed';
const AdvancedSearch = lazy(() => import('../AdvancedSearch'));
import AdvancedSearchAllowed from '../AdvancedSearch/allowed';
import { AccountSelf, Self } from 'App/resources/AllIWant';
const Admin = lazy(() => import('../Admin/AdminRoutes'));
import ListProjectPlanAllowed from 'App/pages/ListProjectPlans/allowed';
const ListProjectPlan = lazy(() => import('App/pages/ListProjectPlans'));
import ProjectMapAllowed from 'App/pages/Map/ProjectMap/allowed';
const ProjectMap = lazy(() => import('App/pages/ProjectBoard/ProjectBoardRouter'));
const CompanyMap = lazy(() => import('App/pages/Map/CompanyMap'));
import ProjectBoardAllowed from 'App/pages/ProjectBoard/allowed';
const ProjectBoard = lazy(() => import('App/pages/ProjectBoard/ProjectBoardRouter'));
const Matcher = lazy(() => import('../Matcher/Matcher'));
const SupportList = lazy(() => import('../SupportList'));
import SupportListAllowed from '../SupportList/allowed';
import InsightsAllowed from 'Components/Insights/InsightsAllowed';
import MatcherAllowed from '../Matcher/allowed';
const MailCampaign = lazy(() => import('App/pages/MailCampaign/MailCampaign'));
const FindProspects = lazy(() => import('App/pages/FindProspects/FindProspects'));
import FindProspectsAllowed from '../FindProspects/allowed';
import MailCampaignAllowed from '../MailCampaign/allowed';
const Insights = lazy(async () => import('Components/Insights/Insights').then(m => ({ default: m.PortedInsights })));
const OAuthLanding = lazy(() => import('App/components/OAuthLanding/OAuthLanding'));
const ActivateIntegration = lazy(() => import('App/components/ActivateIntegration/ActivateIntegration'));
const PortedManageAccount = lazy(async () =>
	import('App/pages/ManageAccount/ManageAccount').then(m => ({ default: m.PortedManageAccount }))
);
const PortedLandingpageEditorRoute = lazy(async () =>
	import('App/components/LandingpageEditor/LandingpageEditor').then(m => ({
		default: m.PortedLandingpageEditorRoute
	}))
);
const EditFlow = lazy(async () =>
	import('App/pages/EditFlowReactWrapper/EditFlowReactWrapper').then(m => ({ default: m.PortedEditFlow }))
);
const FlowEditor = lazy(async () => import('App/pages/FlowEditor/FlowEditor'));
const OnboardingRoute = lazy(() => import('App/pages/Onboarding/OnboardingRoute'));
const OpenDispatcher = lazy(() => import('App/pages/OpenDispatcher/OpenDispatcher'));
const UserInviteRoute = lazy(() => import('App/pages/UserInvite/UserInviteRoute'));
const WhoCalled = lazy(() => import('App/pages/WhoCalled'));
const AdminInternalSalesboard = lazy(
	() => import('App/pages/Admin/subPages/AdminInternalSalesboard/AdminInternalSalesboard')
);
const AIAgent = lazy(() => import('App/pages/AIAgent/AIAgent'));
const AIWorkspace = lazy(() => import('App/shadcn/pages/AIWorkspace/AIWorkspace'));
const ProspectingAgent = lazy(() => import('App/shadcn/pages/ProspectingAgent/ProspectingAgent'));
const ProspectingWorkspace = lazy(() => import('App/shadcn/pages/ProspectingWorkspace/ProspectingWorkspace'));
const AgentPagesPage = lazy(() => import('App/shadcn/pages/AIWorkspace/Chat/AgentPagesPage'));
const LiveCoach = lazy(() => import('App/shadcn/pages/LiveCoach/LiveCoach'));

export const isAdmin = () => {
	const self = AppService.getSelf();
	return self.administrator;
};

const useAIAgentAccess = () => {
	const hasAI = useFeatureAvailable(Feature.AI);
	const hasWorkflows = useSoftDeployAccess('WORKFLOWS');
	const self = useSelector(({ App }: RootState) => App.self);
	const hasAgentUserAccess = self?.userParams.agentUserAccess ?? false;
	return (isAdmin() || hasAgentUserAccess) && hasAI && hasWorkflows;
};

// WEB-6595: AI Workspace requires the `aiAssistant` feature (which after this
// PR is granted only by the new Upsales AI license tier). AI_ASSISTANT
// soft-deploy is the internal escape hatch — when removed, only the feature
// gates access.
const useAIWorkspaceAccess = () => {
	const hasAiAssistant = useFeatureAvailable(Feature.AI_ASSISTANT);
	const hasAssistantSoftDeploy = useSoftDeployAccess('AI_ASSISTANT');
	return hasAiAssistant || hasAssistantSoftDeploy;
};

// WEB-10482: the AI licence is `useAIWorkspaceAccess`, same as every other
// surface in the AI section. This used to ask for `Feature.AI` instead — the
// Accelerate feature, unrelated to `aiAssistant` since WEB-6595 — so an account
// given AI through the `AI_ASSISTANT` flag or an `UPSALES_AI` trial addon got the
// workspace and the chat but silently no prospecting, with no way to grant it
// short of changing their version.
const useProspectingAgentAccess = () => {
	const hasAiLicense = useAIWorkspaceAccess();
	const hasProspectingAgent = useSoftDeployAccess('PROSPECTING_AGENT');
	return hasAiLicense && hasProspectingAgent;
};

// V2 needs the V1 flag too: every prospecting_* tool in TOOL_REGISTRY is gated
// on PROSPECTING_AGENT, so a V2-only account would get a session where the
// assistant can talk but not search. The API guard on the session routes makes
// the same demand.
const useProspectingWorkspaceAccess = () => {
	const hasProspectingAgentAccess = useProspectingAgentAccess();
	const hasV2 = useSoftDeployAccess('PROSPECTING_AGENT_V2');
	return hasProspectingAgentAccess && hasV2;
};

// The Pages editor. Open to the account's own users when `AGENT_PAGES` is on,
// and to a consultant session regardless — that's how we build a Page inside a
// customer's tenant without switching the feature on for them (WEB-10013).
const useAgentPagesAccess = () => usePagesAuthoringAccess();

// Real-time negotiation liveCoach (WEB-6306). Gate at the `allowed` level only,
// NOT via `filter`. `filter` is evaluated once when <Routes> first mounts —
// on a hard-reload deep-link to /live-coach, that happens before Redux has
// hydrated `accountSelf` (initial API bootstrap is still in flight), so the
// flag reads as false and the route is permanently excluded from the Switch.
// `allowed` is a hook called inside AllowedWrapper, which subscribes to
// Redux via useSelector and re-renders as state hydrates.
const useLiveCoachAccess = () => useSoftDeployAccess('AI_NEGOTIATION_COPILOT');

// Notify analytics that the location have changed
export const notifyAnalytics = () => {
	// Disabled since this means we double track everything.
	// When all routes are in react we can do tracking from here
	// and remove the tracking in app/upsales/app.js
	// import Analytics from 'App/services/Analytics';
	// Analytics.page();
};

history.listen?.(() => notifyAnalytics());

export type RouteConfig = {
	path: string;
	component: React.LazyExoticComponent<any> | React.ComponentType<any>;
	sub?: string[];
	queryParams?: string;
	/** Used when setting up compatibility with angular */
	name?: string;
	/** Should be set to true for ported routes have the same path as an old angular route.
	 * This skips setting up an angular react-root-* compatibility state in app.js */
	sameAngularPath?: boolean;
	section?: 'sale' | 'market' | 'followup' | 'admin' | 'support' | 'portfolio' | 'ai';
	allowed?: () => boolean;
	public?: boolean;
	/** if present, it should return true if the route should be shown.
	 * Use it when having the same routing path for different sections based on conditions
	 */
	filter?: () => boolean;
};

export const routes: RouteConfig[] = [
	{
		path: '/group-mail-editor/:id',
		component: GroupMailEditor,
		sub: ['/design', '/recipients', '/settings'],
		section: 'market'
	},
	{
		path: '/ai-agent',
		name: 'aiAgent',
		component: AIAgent,
		section: 'admin',
		allowed: useAIAgentAccess
	},
	{
		path: '/ai-workspace',
		name: 'aiWorkspace',
		component: AIWorkspace,
		section: 'ai',
		allowed: useAIWorkspaceAccess
	},
	...(
		[
			['chat', 'aiWorkspaceChat'],
			['chat/:id', 'aiWorkspaceChatConversation'],
			['agents', 'aiWorkspaceAgents'],
			['agents/:id', 'aiWorkspaceAgent'],
			['routines', 'aiWorkspaceRoutines'],
			['org', 'aiWorkspaceOrg'],
			['settings', 'aiWorkspaceSettings'],
			['settings/:tab', 'aiWorkspaceSettingsTab'],
			['usage', 'aiWorkspaceUsage'],
			['history', 'aiWorkspaceHistory'],
			['inbox', 'aiWorkspaceInbox']
		] as const
	).map(([sub, name]) => ({
		path: `/ai-workspace/${sub}`,
		name,
		component: AIWorkspace,
		section: 'ai' as const,
		allowed: useAIWorkspaceAccess
	})),
	{
		path: '/pages/:slug',
		name: 'agentPageView',
		component: PageView
	},
	// Deep-link route must precede the bare /prospecting-agent route: <Switch>
	// matches the first route whose path matches, and neither is `exact`, so a
	// bare-path-first ordering would swallow /runs/:runId before useParams sees it.
	{
		path: '/prospecting-agent/runs/:runId',
		name: 'prospectingAgentRun',
		component: ProspectingAgent,
		section: 'ai',
		filter: () => hasSoftDeployAccess('AI_WORKSPACE_PAGE'),
		allowed: useProspectingAgentAccess
	},
	{
		path: '/prospecting-agent/runs/:runId',
		name: 'prospectingAgentRun',
		component: ProspectingAgent,
		section: 'sale',
		filter: () => !hasSoftDeployAccess('AI_WORKSPACE_PAGE'),
		allowed: useProspectingAgentAccess
	},
	{
		path: '/prospecting-agent',
		name: 'prospectingAgent',
		component: ProspectingAgent,
		section: 'ai',
		filter: () => hasSoftDeployAccess('AI_WORKSPACE_PAGE'),
		allowed: useProspectingAgentAccess
	},
	{
		path: '/prospecting-agent',
		name: 'prospectingAgent',
		component: ProspectingAgent,
		section: 'sale',
		filter: () => !hasSoftDeployAccess('AI_WORKSPACE_PAGE'),
		allowed: useProspectingAgentAccess
	},
	// The V2 workspace (WEB-9400) is its own screen on its own path, not a mode
	// of the V1 one. Gated at the `allowed` level only, like /live-coach above:
	// `filter` runs once when <Routes> mounts, which on a hard-reload deep-link
	// happens before Redux has the flags, so a `filter` gate would drop the
	// route permanently.
	{
		path: '/prospecting-workspace',
		name: 'prospectingWorkspace',
		component: ProspectingWorkspace,
		section: 'ai',
		allowed: useProspectingWorkspaceAccess
	},
	{
		// Standalone Pages tab only exists in "page mode" (AI_WORKSPACE_PAGE on) —
		// the nav item is gated on the same flag (Navbar.tsx). Keep the route when
		// the flag is ON, matching the section 'ai' page-mode entries above; when
		// the flag is off, Pages is reached via the AI Workspace sidebar instead.
		// (Was inverted, which dropped the route exactly when the tab was shown,
		// leaving a blank page.)
		path: '/agent-pages',
		name: 'agentPages',
		component: AgentPagesPage,
		section: 'ai',
		filter: () => hasSoftDeployAccess('AI_WORKSPACE_PAGE'),
		allowed: useAgentPagesAccess
	},
	{
		path: '/live-coach',
		name: 'live-coach',
		component: LiveCoach,
		section: 'ai',
		allowed: useLiveCoachAccess
	},
	{
		path: '/calendar/',
		queryParams: '?date?selected',
		component: CalendarView,
		name: 'calendar',
		section: 'sale'
	},
	{
		path: '/mail-template-editor/:id',
		component: MailTemplateEditor,
		sub: ['/design', '/settings'],
		section: 'market'
	},
	{
		path: '/marketingboard',
		component: MarketingBoard,
		allowed: MarketingBoardAllowed,
		section: 'market',
		name: 'marketingboard'
	},
	{
		path: '/salesboard/:id',
		component: Salesboard,
		allowed: SalesboardAllowed,
		section: 'sale',
		name: 'salesboard'
	},
	{
		path: '/salesboard/',
		component: Salesboard,
		allowed: SalesboardAllowed,
		section: 'sale',
		name: 'salesboard'
	},
	{
		path: '/:customerId/detected-duplicates/:entity',
		component: DetectedDuplicates,
		allowed: isAdmin,
		name: 'detected-duplicates',
		section: 'sale'
	},
	{
		path: '/detected-duplicates-landing-page/:entity',
		component: DetectedDuplicatesLandingPage,
		allowed: isAdmin,
		name: 'detected-duplicates-landing-page',
		section: 'sale'
	},
	{
		path: '/changebrand/:id',
		component: ChangeBrands,
		name: 'change-brand',
		section: 'sale'
	},
	{
		path: '/form-editor/:id',
		component: FormEditor,
		section: 'market'
	},
	{
		path: '/form-overview/:id',
		component: FormOverview,
		name: 'form-overview',
		section: 'market'
	},
	{
		path: '/:customerId/events/edit/:id?',
		component: EditSocialEvent,
		name: 'editSocialEvent',
		allowed: EditSocialEventAllowed,
		section: 'market'
	},
	{
		path: '/task',
		name: 'task',
		component: TaskList,
		allowed: TaskListAllowed,
		section: 'sale'
	},
	{
		path: '/mailtemplates',
		name: 'mailtemplates',
		component: MailTemplates,
		allowed: MailTemplatesAllowed,
		section: 'market'
	},
	{
		path: '/userdefinedobjects/:typeId',
		name: 'userdefinedobjects',
		component: ListUserDefinedObjects,
		allowed: ListUserDefinedObjectsAllowed,
		section: 'sale'
	},
	{
		path: '/rc/:dashboardId?',
		name: 'reportcenter',
		component: Reportcenter,
		allowed: ReportcenterAllowed,
		section: 'followup'
	},
	{
		// Deep-link dispatcher. The 4-segment form must be declared BEFORE the
		// 2-segment one: the Switch matches in order with no `exact`, so `/open/:type/:id`
		// would otherwise match a mention-email link and drop its background page.
		path: '/open/:type/:id/:backgroundType/:backgroundId',
		name: 'openWithBackground',
		component: OpenDispatcher
	},
	{
		// External contract: CopyableId, the ticket "copy link" action and five
		// backend producers all emit this url. It must not change.
		path: '/open/:type/:id',
		name: 'open',
		component: OpenDispatcher
	},
	{
		// Pre-login: there is no session or tenant when an invitee opens this link,
		// hence `public: true` (the react equivalent of the angular shell's
		// `ignoreAppService`). The url is external-facing — admins copy it out of
		// AdminActiveInvites — so it must stay exactly `/invite`.
		path: '/invite',
		queryParams: '?code?customer?lang',
		name: 'invite',
		component: UserInviteRoute,
		public: true,
		allowed: () => true
	},
	{
		path: '/tv/:id?',
		name: 'reportcenterPublicWithId',
		component: ReportcenterPublic,
		public: true,
		allowed: () => true
	},
	{
		path: '/tv',
		name: 'reportcenterPublic',
		component: ReportcenterPublic,
		public: true,
		allowed: () => true
	},
	{
		path: '/signals',
		queryParams: '?id',
		name: 'signals',
		component: Signals,
		allowed: SignalsAllowed,
		section: 'sale'
	},
	{
		// Must precede '/campaigns' — the <Switch> matches the first non-exact path,
		// so the bare list route would otherwise swallow '/campaigns/:id/:page?'.
		path: '/campaigns/:id/:page?',
		name: 'campaignCard',
		component: CampaignCard,
		allowed: CampaignCardAllowed,
		section: 'sale'
	},
	{
		path: '/campaigns',
		name: 'campaigns',
		component: ListCampaigns,
		section: 'sale'
	},
	{
		path: '/callLists',
		name: 'calllists',
		component: ListCampaigns,
		allowed: CallListsAllowed,
		section: 'sale'
	},
	{
		path: '/forms',
		name: 'forms',
		component: ListForms,
		allowed: ListFormsAllowed,
		section: 'market'
	},
	{
		path: '/landingpages',
		name: 'landingpages',
		component: ListLandingPages,
		allowed: ListLandingPagesAllowed,
		section: 'market'
	},
	{
		path: '/mail',
		name: 'mail',
		component: ListMail,
		section: 'sale'
	},
	{
		path: '/mail-campaigns',
		name: 'mail-campaigns',
		component: ListMailCampaigns,
		allowed: ListMailCampaignsAllowed,
		section: 'market'
	},
	{
		path: '/:customerId/mail-campaign/:id/:subPage?',
		name: 'mail-campaigns',
		queryParams: '?filter?drill',
		component: MailCampaign,
		sameAngularPath: true,
		allowed: MailCampaignAllowed,
		section: 'market'
	},
	{
		path: '/events',
		name: 'events',
		component: ListSocialEvents,
		allowed: ListSocialEventsAllowed,
		section: 'market'
	},
	{
		path: '/prospecting',
		queryParams: '?box',
		name: 'prospecting',
		component: Prospecting,
		allowed: ProspectingAllowed,
		section: 'sale'
	},
	{
		path: '/:customerId/soliditet/matcher',
		name: 'matcher',
		queryParams: '?campaignId',
		component: Matcher,
		allowed: MatcherAllowed,
		section: 'sale'
	},
	{
		path: '/flows',
		name: 'flows',
		component: ListFlows,
		allowed: ListFlowsAllowed,
		section: 'market'
	},
	{
		path: '/companyGroup/:prospectingId/:view?',
		name: 'companyGroup',
		component: CompanyGroupCard,
		allowed: CompanyGroupCardAllowed,
		section: 'sale'
	},
	{
		path: '/segments',
		name: 'segments',
		component: ListSegments,
		section: 'market'
	},
	{
		path: '/form-submits',
		name: 'formSubmits',
		queryParams: '?f',
		component: FormSubmits,
		allowed: FormSubmitsAllowed,
		section: 'market'
	},
	{
		path: '/orders',
		name: 'orders',
		component: ListOrders,
		allowed: ListOrdersAllowed,
		section: 'sale'
	},
	{
		path: '/activities',
		name: 'activities',
		component: ListActivities,
		allowed: ListActivitiesAllowed,
		section: 'sale'
	},
	{
		path: '/appointments',
		name: 'appointments',
		component: ListAppointments,
		allowed: ListAppointmentsAllowed,
		section: 'sale'
	},
	{
		path: '/visitors',
		name: 'visitors',
		component: ListVisitors,
		allowed: ListVisitorsAllowed,
		section: 'market'
	},
	{
		path: '/leads2',
		name: 'leads',
		component: ListLeads,
		allowed: ListLeadsAllowed,
		section: 'market'
	},
	{
		path: '/companies',
		name: 'companies',
		component: AccountGrowth,
		section: 'sale'
	},
	{
		path: '/company-map',
		name: 'companyMap',
		component: CompanyMap,
		section: 'sale'
	},
	{
		// Must precede '/contacts' — the <Switch> matches the first non-exact path,
		// so the bare list route would otherwise swallow '/contacts/:id/:page?' and
		// render the contact list instead of the card. Same trap as '/campaigns'.
		path: '/contacts/:id/:page?',
		name: 'contactCard',
		component: ContactCard,
		allowed: ContactCardAllowed,
		section: 'sale'
	},
	{
		path: '/contacts',
		name: 'contacts',
		component: ListContacts,
		section: 'sale'
	},
	{
		path: '/agreements',
		name: 'agreements',
		component: ListAgreements,
		section: 'sale'
	},
	{
		path: '/projects',
		name: 'projects',
		component: ListProjectPlan,
		allowed: ListProjectPlanAllowed,
		section: 'sale'
	},
	{
		path: '/project-board/:id',
		name: 'projectBoard',
		component: ProjectBoard,
		allowed: ProjectBoardAllowed,
		section: 'sale'
	},
	{
		path: '/project-board/',
		name: 'projectBoard',
		component: ProjectBoard,
		allowed: ProjectBoardAllowed,
		section: 'sale'
	},
	{
		path: '/project-map/:id',
		name: 'projectMap',
		component: ProjectMap,
		allowed: ProjectMapAllowed,
		section: 'sale'
	},
	{
		path: '/project-map/',
		name: 'projectMap',
		component: ProjectMap,
		allowed: ProjectMapAllowed,
		section: 'sale'
	},
	{
		path: '/opportunities',
		name: 'opportunities',
		component: ListOpportunities,
		allowed: ListOpportunitiesAllowed,
		section: 'sale'
	},
	{
		path: '/periodizations',
		name: 'periodizations',
		component: ListPeriodizations,
		allowed: ListPeriodizationsAllowed,
		section: 'sale'
	},
	{
		path: '/phoneCalls',
		name: 'phonecalls',
		component: ListPhoneCalls,
		allowed: ListPhoneCallsAllowed,
		section: 'sale'
	},
	{
		path: '/accounts/:id/:page?',
		name: 'clientCard',
		component: ClientCard,
		allowed: ClientCardAllowed,
		section: 'sale'
	},
	{
		path: '/document-template-editor/:id',
		component: DocumentTemplateEditor,
		sub: ['/details', '/design'],
		section: 'sale'
	},
	{
		path: '/customerPortfolio/:tab?',
		name: 'customerPortfolio',
		component: CustomerPortfolio,
		allowed: CustomerPortfolioAllowed,
		section: 'sale'
	},
	{
		path: '/advanced-search/:type?',
		name: 'advancedSearch',
		component: AdvancedSearch,
		allowed: AdvancedSearchAllowed,
		section: 'sale'
	},
	{
		path: '/support/:view?',
		name: 'support',
		component: SupportList,
		allowed: SupportListAllowed,
		section: 'support'
	},
	{
		path: '/admin/:page?/:subpage?',
		name: 'admin',
		sameAngularPath: true,
		component: Admin,
		section: 'admin'
	},
	{
		path: '/admin-internal/salesboard/:id',
		name: 'internalSalesboard',
		component: AdminInternalSalesboard,
		allowed: isAdmin,
		section: 'admin'
	},
	{
		path: '/:customerId/insights/:type?',
		queryParams: '?id?useNext',
		allowed: InsightsAllowed,
		sameAngularPath: true,
		component: Insights,
		section: 'followup'
	},
	{
		path: '/oauthLanding/:integrationId/:type?',
		queryParams: 'code?oAuthFieldName',
		sameAngularPath: true,
		component: OAuthLanding,
		section: 'admin'
	},
	{
		// OAuth return landing for app activation. The url is registered as a redirect
		// uri with third-party providers, so it must stay exactly this. Fortnox sends
		// users here before they have a session — hence `public: true`, the react
		// equivalent of the angular state having had no auth resolve.
		path: '/activate-app/:integrationAlias',
		sameAngularPath: true,
		component: ActivateIntegration,
		public: true,
		allowed: () => true
	},
	{
		path: '/manageAccount',
		sameAngularPath: true,
		component: PortedManageAccount,
		section: 'sale'
	},
	{
		path: '/:customerId/onboarding',
		queryParams: '?openFullScreen?initialImportApp?initialSubstepId?oauthCode',
		component: OnboardingRoute,
		name: 'onboarding'
	},
	{
		path: '/:customerId/forms/landingpageEditor/:id?',
		queryParams: '?copy',
		component: PortedLandingpageEditorRoute,
		section: 'market'
	},
	{
		path: '/:customerId/flows/:id/',
		component: EditFlow,
		sameAngularPath: true,
		section: 'market'
	},
	{
		path: '/:customerId/flow/standalone/:id',
		queryParams: '?preset?segmentId?showSelectionTooltip',
		component: FlowEditor,
		sameAngularPath: true,
		section: 'market'
	},
	{
		path: '/:customerId/flow/:segmentId',
		queryParams: '?preset?showSelectionTooltip',
		component: FlowEditor,
		sameAngularPath: true,
		section: 'market'
	},
	{
		path: '/who-called/:number',
		component: WhoCalled,
		section: 'sale'
	},
	{
		path: '/find-prospects/',
		component: FindProspects,
		allowed: FindProspectsAllowed,
		filter: () => {
			const accountSelf = AppService.getAccountSelf();
			if (!accountSelf) return true;
			return !(!accountSelf.products.crm && accountSelf.features.soliditet);
		},
		section: 'sale'
	},
	{
		path: '/find-prospects/',
		component: FindProspects,
		allowed: FindProspectsAllowed,
		filter: () => {
			const accountSelf = AppService.getAccountSelf();
			if (!accountSelf) return false;
			return !accountSelf.products.crm && accountSelf.features.soliditet;
		},
		section: 'market'
	}
];

type RedirectRouteConfig = {
	from: string;
	/** Should match a valid react route (e.g. one from the list above) */
	to: string;
	/** Only used when setting up compatibility with angular */
	queryParams?: string;
	exact?: boolean;
	/** Function to decide if the reroute should happen. You can use this to check feature flags */
	shouldRedirect?: (scope: { params: Record<string, string | number>; search: URLSearchParams }) => boolean;
	paramMapper?: (scope: {
		params: Record<string, string | number>;
		search: URLSearchParams;
	}) => Record<string, string | number>;
	searchParamsMapper?: (scope: {
		params: Record<string, string | number>;
		search: URLSearchParams;
	}) => URLSearchParams;
	debug?: boolean;
};

// Routes that can redirect to a different url. This is mainly used for keeping compatibility with old angular routes
export const redirectRoutes: RedirectRouteConfig[] = [
	{
		from: '/todo/:view?',
		to: '/task'
	},
	{
		from: '/:customerId(\\d+)/contacts/',
		to: '/contacts',
		exact: true
	},
	{
		from: '/:customerId(\\d+)/admin/salesboards',
		to: '/admin/salesboards'
	},
	{
		from: '/:customerId(\\d+)/agreement/',
		to: '/agreements'
	},
	{
		from: '/:customerId(\\d+)/phoneCalls/',
		to: '/phoneCalls'
	},
	{
		from: '/:customerId(\\d+)/admin/salesboard/:id',
		to: '/admin-internal/salesboard/:id'
	},
	{
		from: '/:customerId(\\d+)/accounts/',
		to: '/companies',
		exact: true
	},
	{
		from: '/:customerId(\\d+)/up-admin/journeyEditor/:type?/:id?',
		to: '/admin/journeyEditor/:type?/:id?'
	},
	{
		from: '/:customerId(\\d+)/up-admin/integrations/edit/:id?',
		to: '/admin/integrations/edit/:id?'
	},
	{
		from: '/:customerId(\\d+)/up-admin/integrations/configure/:id/:configure?',
		to: '/admin/integrations/configure/:id/:configure?',
		queryParams: '?code?setOauthToNull?tab?oAuthFieldName'
	},
	{
		from: '/:customerId(\\d+)/find-prospects/',
		to: '/find-prospects/',
		shouldRedirect: () => hasSoftDeployAccess('PORTED_ROUTE_FIND_PROSPECTS')
	},
	{
		from: '/:customerId(\\d+)/activities/',
		to: '/activities',
		exact: true,
		shouldRedirect: () => hasSoftDeployAccess('REACT_ACTIVITIES_LIST')
	},
	{
		from: '/:customerId(\\d+)/appointments/',
		to: '/appointments',
		exact: true,
		shouldRedirect: () => hasSoftDeployAccess('REACT_APPOINTMENTS_LIST')
	},
	{
		from: '/:customerId(\\d+)/periodizations/',
		to: '/periodizations',
		exact: true,
		shouldRedirect: () => hasSoftDeployAccess('REACT_PERIODIZATIONS_PAGE')
	},
	{
		from: '/:customerId(\\d+)/mail/templates/',
		to: '/mailtemplates'
	},
	{
		from: '/:customerId(\\d+)/up-admin/:page?/:subpage?',
		to: '/admin/:page/:subpage?',
		shouldRedirect: ({ params }) => {
			const { page = 'dashboard', subpage } = params;

			if (page === 'dashboard') {
				return true;
			}
			if (page === 'import' && subpage === undefined) {
				return true;
			}
			if (page === 'import' && subpage !== undefined) {
				return true;
			}
			if (page === 'domains') {
				return true;
			}
			if (page === 'reminders') {
				return true;
			}
			if (page === 'default-mail-signature') {
				return true;
			}
			if (page === 'journeyStatuses') {
				return true;
			}
			if (page === 'notification-center') {
				return true;
			}
			if (page === 'optin') {
				return true;
			}
			if (page === 'sign-in-stats') {
				return true;
			}
			if (page === 'triggers') {
				return true;
			}
			if (page === 'appointment-availability') {
				return true;
			}
			if (page === 'prospecting-signals') {
				return true;
			}
			if (page === 'salesprocesses') {
				return true;
			}
			if (page === 'automation') {
				return true;
			}
			if (page === 'profile') {
				return true;
			}
			if (page === 'pipeline-goals') {
				return true;
			}
			if (page === 'booked-appointment-goals') {
				return true;
			}
			if (page === 'fields') {
				return true;
			}
			if (page === 'activitytypes') {
				return true;
			}
			if (page === 'mailsettings') {
				return true;
			}
			if (page === 'optOut') {
				return true;
			}
			if (page === 'orderstages') {
				return true;
			}
			if (page === 'automations') {
				return true;
			}
			if (page === 'products') {
				return true;
			}
			if (page === 'activeInvites') {
				return true;
			}
			if (page === 'looker') {
				return true;
			}
			if (page === 'scripts' || page === 'script') {
				return true;
			}
			if (page === 'security' && subpage === 'apiKeys') {
				return true;
			}
			if (page === 'sharedviews') {
				return true;
			}
			if (page === 'security' && subpage === '2fa') {
				return true;
			}
			if (page === 'usersAndRoles') {
				return true;
			}
			if (page === 'user') {
				return true;
			}
			if (page === 'currencies') {
				return true;
			}
			if (page === 'billing') {
				return true;
			}
			if (page === 'documentTemplates') {
				return true;
			}
			if (page === 'integrations' && !subpage) {
				return true;
			}
			if (page === 'mailSignature') {
				return true;
			}
			if (page === 'visit') {
				return true;
			}
			if (page === 'brands') {
				return true;
			}
			if (page === 'accountProfile') {
				return true;
			}
			if (page === 'ip-block') {
				return true;
			}
			if (page === 'shared-appointment-availability') {
				return true;
			}
			if (page === 'salesboard-card') {
				return true;
			}
			if (page === 'pricelists') {
				return true;
			}
			if (page === 'accountsettings') {
				return true;
			}
			if (page === 'activity-quota') {
				const hasGoals = hasSoftDeployAccess('GOALS');
				const hasToDo = hasSoftDeployAccess('TODO_LIST');
				return !(hasGoals && hasToDo);
			}
			if (page === 'appointment-editor') {
				return true;
			}
			if (page === 'sales-quota') {
				return true;
			}
			if (page === 'salesboards') {
				return true;
			}
			if (page === 'deletelog') {
				return true;
			}
			if (page === 'salesboard') {
				return true;
			}
			if (page === 'completed-appointment-goals') {
				return true;
			}
			if (page === 'dynamiclinks') {
				return true;
			}
			if (page === 'phonecall-goals') {
				return true;
			}
			if (page === 'trigger') {
				return true;
			}
			if (page === 'listsalesboards') {
				return true;
			}

			return false;
		},
		paramMapper: ({ params }) => {
			const { page = 'dashboard' } = params;
			const paramRes = { page, subpage: params.subpage };

			if (page === 'default-mail-signature') {
				paramRes.page = 'email-signatures';
			}
			if (page === 'notification-center') {
				paramRes.page = 'notifications';
			}

			return paramRes;
		}
	}
];

const RedirectRoute = ({ redirectConfig }: { redirectConfig: (typeof redirectRoutes)[number] }) => {
	const { params } = useRouteMatch();
	const { search } = useLocation();

	const query = useMemo(() => new URLSearchParams(search), [search]);

	const {
		from,
		to,
		shouldRedirect = () => true,
		paramMapper = ({ params }) => params,
		searchParamsMapper = ({ search }) => search,
		debug = false,
		exact = false
	} = redirectConfig;

	if (debug) {
		console.log('Hit redirect route', {
			from,
			to,
			params,
			search
		});
	}

	const routeScope = { params, search: query };

	if (!shouldRedirect(routeScope)) {
		if (debug) {
			console.log('shouldRedirect() returned false - aborting redirect');
		}
		return null;
	}

	const mappedParams = paramMapper(routeScope);
	const finalSearch = searchParamsMapper(routeScope)?.toString() || '';

	let finalPath = generatePath(to, mappedParams);

	if (finalSearch.length > 0) {
		finalPath += `?${finalSearch}`;
	}

	if (debug) {
		console.log(`Redirecting to ${finalPath}`, { mappedParams });
	}

	return <Redirect key={from} to={finalPath} exact={exact} />;
};

export const getSectionFromPath = (
	path: string,
	accountSelf: AccountSelf,
	self: Self,
	agentPages: AgentPage[] = []
) => {
	let section = null;

	// A Page's section is data, not route config: one route serves every Page, and
	// a navTab Page names the section it belongs to (WEB-9222). Resolved before the
	// static lookup so the tab the user clicked stays highlighted; a Page with no
	// section falls through and highlights nothing, as before. The path comes from
	// the route entry rather than a repeated literal, so renaming the route can't
	// silently break the highlight. `navSection` is non-null only for navTab pages
	// (enforced in the AgentPage repo), so it doubles as the placement check.
	const pageRoute = routes.find(r => r.name === 'agentPageView');
	const pageMatch = pageRoute && matchPath<{ slug: string }>(path, { path: pageRoute.path });
	if (pageMatch) {
		const navSection = agentPages.find(p => p.slug === pageMatch.params.slug)?.navSection;
		if (navSection) {
			return navSection;
		}
	}

	// Match path against all out route configs
	section = routes
		.filter(r => !r.filter || r.filter())
		.find(r => (matchPath(path, { path: r.path }) ? r : null))?.section;

	// As long as we have our angular routes we also need to look there
	if (!section) {
		const state = Tools.$state.current;
		section = state.section;
		// This is stupid and should probably be removed when we have all routes in react
		if (state.sectionFn && typeof state.sectionFn === 'function') {
			section = state.sectionFn(accountSelf, self, Tools.$state.params, Tools.$location);
		}
	}

	return (section as NonNullable<RouteConfig['section']>) || null;
};

const Routes = () => {
	// Route `filter`s read feature flags via FeatureHelperStandalone, which is not
	// reactive. On a cold load the switch is built before flags hydrate, so
	// flag-gated routes (e.g. /agent-pages) evaluate their filter to false and are
	// dropped — the Angular state resolves but react-router has no matching route,
	// leaving a blank page until a manual refresh once flags are warm (WEB-7819).
	// Subscribing to accountSelf.unreleasedFeatures re-renders (and re-filters) as
	// soon as flags land, so the route appears without a refresh.
	const unreleasedFeatures = useSelector(({ App }: RootState) => App.accountSelf?.unreleasedFeatures);
	const visibleRoutes = useMemo(
		() => routes.filter(r => !r.filter || r.filter()),
		// Recompute when flags hydrate; the filters read them via FeatureHelperStandalone.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[unreleasedFeatures]
	);
	return (
		<Suspense fallback={<LoaderPage />}>
			<Switch>
				{redirectRoutes.map(r => (
					<ReactRoute
						key={r.from}
						path={r.from}
						exact={r.exact}
						component={() => <RedirectRoute redirectConfig={r} />}
					></ReactRoute>
				))}
				{visibleRoutes.map(r => (
					<Route key={r.path} allowed={r.allowed} path={r.path} public={r.public} component={r.component} />
				))}
				{/* fallback route when in angular */}
				<Route component={() => null} />
			</Switch>
		</Suspense>
	);
};

export default Routes;
